import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, Trash2 } from 'lucide-react';
import ModelViewer from '../components/ModelViewer';
import { ActionBtn } from '../components/ReorderControls';
import { fileUrl, textureMaps, saveDisplay, listStylePresets, createStylePreset, deleteStylePreset } from '../lib/api';
import { useEscape } from '../hooks/useEscape';
import LightRig from './LightRig';
import { rigFor, presetRig } from '../lib/lights';

const SHADING = [['normal', 'NORMAL'], ['matcap', 'MATCAP'], ['toon', 'TOON']];

const fromItem = (it) => ({
  shading: it.shading || 'normal',
  lighting: it.lighting || 'balanced',
  lights: rigFor(it),
  brightness: it.brightness ?? 1,
  outline: !!it.outline,
  outline_width: it.outline_width ?? 2,
  outline_color: it.outline_color || '#000000'
});

export default function StyleEditor({ it, onClose, onSaved }) {
  const [d, setD] = useState(() => fromItem(it));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const [markers, setMarkers] = useState(true);
  const [rigKey, setRigKey] = useState(0);
  const [openLight, setOpenLight] = useState(() => fromItem(it).lights[0]?.id || null);
  useEscape(onClose);

  const base = useMemo(() => ({ id: it.id, modelUrl: fileUrl(it.filename), textures: textureMaps(it.textures) }), [it.id, it.filename, it.textures]);
  const preview = {
    ...base, shading: d.shading, lighting: d.lighting, lights: d.lights, brightness: d.brightness,
    outline: { on: d.outline, width: d.outline_width, color: d.outline_color }
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try { await saveDisplay(it.id, d); onSaved(); onClose(); }
    catch (e) { setErr(e?.response?.data?.detail?.toString() || 'Save failed'); setBusy(false); }
  };

  const target = document.getElementById('tv-screen-root') || document.body;
  return createPortal(
    <div className="absolute inset-0 z-[100] flex items-stretch sm:items-center justify-center bg-[#0a0502]/94 backdrop-blur-sm p-2 sm:p-5" onClick={onClose}>
      <div className="relative w-full max-w-5xl max-h-full flex flex-col rounded-lg border-2 border-[#3a2512] bg-[#1a0e05] lightbox-in"
           onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Style for ${it.title}`} data-testid="style-editor">
        <div className="flex items-center justify-between gap-3 p-3 sm:p-4 border-b border-[#3a2512]">
          <div className="min-w-0">
            <div className="font-osd text-[11px] text-[#f5a623] tracking-widest">// STYLE · SHADING &amp; LIGHTING</div>
            <div className="font-display text-lg sm:text-2xl text-[#ffe9b8] truncate">{it.title}</div>
          </div>
          <ActionBtn onClick={onClose} label="Close" testId="style-editor-close"><X className="w-5 h-5" /></ActionBtn>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 grid grid-cols-1 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-4">
          <div className="h-[220px] sm:h-[320px] md:h-[420px] sticky -top-3 sm:-top-4 z-10 rounded-lg overflow-hidden border-2 border-[#3a2512]" data-testid="style-editor-preview">
            <ModelViewer item={preview} expanded fillContainer showLights={markers} highlightLight={openLight} />
            <MarkersSwitch on={markers} onChange={setMarkers} />
          </div>
          <div className="min-w-0 space-y-5">
            <PresetBar draft={d} onApply={(style) => {
              const next = fromItem({ ...d, ...style });
              setD((x) => ({ ...x, ...next }));
              setOpenLight(next.lights[0]?.id || null);
              setRigKey((k) => k + 1);
            }} />
            <Group label="SHADING" options={SHADING} value={d.shading} onPick={(v) => set('shading', v)} testId="style-shading" />
            {d.shading === 'matcap' && (
              <div className="font-osd text-[11px] text-[#8b6a3e] tracking-wider " data-testid="style-lighting-hint">LIGHTS DON'T CHANGE MATCAP (OR MODELS EXPORTED AS UNLIT). BRIGHTNESS STILL WORKS.</div>
            )}
            <Slider label="BRIGHTNESS" min={0.3} max={2} step={0.05} value={d.brightness} onChange={(v) => set('brightness', v)}
                    display={`${Math.round(d.brightness * 100)}%`} testId="style-brightness" />
            <OutlineControls d={d} set={set} />
            <LightRig key={rigKey} lights={d.lights} preset={d.lighting} onChange={(v) => set('lights', v)} onOpen={setOpenLight}
                      onPreset={(k) => { const rig = presetRig(k); setD((x) => ({ ...x, lighting: k, lights: rig })); setOpenLight(rig[0].id); setRigKey((n) => n + 1); }} />
          </div>
        </div>

        <div className="p-3 sm:p-4 border-t border-[#3a2512] flex items-center justify-between gap-2 flex-wrap">
          <div className="font-osd text-xs text-[#ff6a3a]" role="alert" data-testid="style-editor-error">{err}</div>
          <div className="flex gap-2 ml-auto">
            <ActionBtn onClick={onClose} label="Cancel" testId="style-editor-cancel">CANCEL</ActionBtn>
            <ActionBtn onClick={save} disabled={busy} label="Save style" tone="accent" testId="style-editor-save">
              <Save className="w-4 h-4" /> {busy ? 'SAVING…' : 'SAVE'}
            </ActionBtn>
          </div>
        </div>
      </div>
    </div>,
    target
  );
}

export function MarkersSwitch({ on, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!on)} aria-pressed={on} data-testid="light-markers-toggle"
            className={`absolute top-2 right-2 z-20 header-tab h-8 px-2 font-osd text-[10px] tracking-widest border-2 rounded-sm ${on ? 'header-tab-active' : ''}`}>
      {on ? 'LIGHTS SHOWN' : 'SHOW LIGHTS'}
    </button>
  );
}

function PresetBar({ draft, onApply }) {
  const [list, setList] = useState(null);
  const [pick, setPick] = useState('');
  const [name, setName] = useState('');
  const [msg, setMsg] = useState(null);
  useEffect(() => { listStylePresets().then(setList).catch(() => setList([])); }, []);
  const chosen = list?.find((p) => p.id === pick);
  const saveAs = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      const p = await createStylePreset(n, draft);
      setList((l) => [...(l || []), p]); setPick(p.id); setName(''); setMsg(`SAVED "${n.toUpperCase()}"`);
    } catch { setMsg('SAVE FAILED'); }
  };
  const remove = async () => {
    if (!chosen) return;
    await deleteStylePreset(chosen.id).catch(() => {});
    setList((l) => l.filter((p) => p.id !== chosen.id)); setPick(''); setMsg(`DELETED "${chosen.name.toUpperCase()}"`);
  };
  return (
    <section className="space-y-2 p-3 border-2 border-[#3a2512] rounded-lg bg-[#0f0704]" data-testid="style-presets">
      <div className="font-osd text-[11px] text-[#8b6a3e] tracking-widest">MY PRESETS <span className="opacity-70">· WHOLE STYLE: SHADING, LIGHTS, BRIGHTNESS, OUTLINE</span></div>
      <div className="flex gap-1.5 flex-wrap">
        <select value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Saved presets" data-testid="style-preset-select"
                className="flex-1 min-w-[140px] h-10 bg-[#1a0e05] border border-[#3a2512] text-[#ffe9b8] font-osd text-xs px-2 rounded">
          <option value="">{list === null ? 'LOADING…' : list.length ? 'PICK A PRESET…' : 'NO PRESETS YET'}</option>
          {(list || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <ActionBtn onClick={() => { onApply(chosen.style); setMsg(`APPLIED "${chosen.name.toUpperCase()}" · SAVE TO KEEP`); }} disabled={!chosen} label="Apply preset" testId="style-preset-apply">APPLY</ActionBtn>
        <ActionBtn onClick={remove} disabled={!chosen} label="Delete preset" tone="danger" testId="style-preset-delete"><Trash2 className="w-4 h-4" /></ActionBtn>
      </div>
      <div className="flex gap-1.5">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="Name this style, e.g. MOODY RIM"
               className="flex-1 min-w-0 h-10 bg-[#1a0e05] border border-[#3a2512] text-[#ffe9b8] font-osd text-xs px-2 rounded" data-testid="style-preset-name" />
        <ActionBtn onClick={saveAs} disabled={!name.trim()} label="Save as preset" testId="style-preset-save">SAVE AS PRESET</ActionBtn>
      </div>
      {msg && <div className="font-osd text-[10px] text-[#9dff7a] tracking-widest" data-testid="style-preset-msg">{msg}</div>}
    </section>
  );
}

function OutlineControls({ d, set }) {
  const toon = d.shading === 'toon';
  return (
    <section className={`space-y-3 p-3 border-2 border-[#3a2512] rounded-lg bg-[#0f0704] ${toon ? '' : 'opacity-50'}`} data-testid="style-outline-section">
      <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
        <input type="checkbox" checked={d.outline} disabled={!toon} onChange={(e) => set('outline', e.target.checked)}
               className="w-5 h-5 accent-[#f5a623] shrink-0" data-testid="style-outline-toggle" />
        <span className="font-osd text-xs tracking-wider text-[#ffe9b8]">INK OUTLINE {!toon && <span className="text-[#8b6a3e]">· TOON ONLY</span>}</span>
      </label>
      {toon && d.outline && (
        <>
          <Slider label="THICKNESS" min={0.5} max={10} step={0.5} value={d.outline_width} onChange={(v) => set('outline_width', v)}
                  display={`${d.outline_width}px`} testId="style-outline-width" />
          <label className="flex items-center gap-3">
            <span className="font-osd text-[11px] text-[#8b6a3e] tracking-widest w-24">COLOUR</span>
            <input type="color" value={d.outline_color} onChange={(e) => set('outline_color', e.target.value)}
                   className="w-12 h-10 bg-transparent border-2 border-[#3a2512] rounded cursor-pointer" data-testid="style-outline-color" />
            <span className="font-osd text-xs text-[#ffe9b8] uppercase">{d.outline_color}</span>
          </label>
        </>
      )}
    </section>
  );
}

function Group({ label, options, value, onPick, testId }) {
  return (
    <div className="space-y-1.5">
      <div className="font-osd text-[11px] text-[#8b6a3e] tracking-widest">{label}</div>
      <div className="flex gap-1.5 flex-wrap" role="radiogroup" aria-label={label}>
        {options.map(([key, text]) => (
          <button key={key} type="button" role="radio" aria-checked={value === key} onClick={() => onPick(key)}
                  className={`header-tab h-10 px-3 font-osd text-xs tracking-wider border-2 rounded-sm ${value === key ? 'header-tab-active' : ''}`}
                  data-testid={`${testId}-${key}`}>
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

function Slider({ label, min, max, step, value, onChange, display, testId }) {
  return (
    <label className="block space-y-1.5">
      <div className="flex justify-between font-osd text-[11px] tracking-widest">
        <span className="text-[#8b6a3e]">{label}</span>
        <span className="text-[#ffe9b8]" data-testid={`${testId}-value`}>{display}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))}
             className="w-full h-8 accent-[#f5a623] cursor-pointer" data-testid={testId} />
    </label>
  );
}
